import { Form, type FormInstance } from 'antd';
import { useCallback, useEffect, useState } from 'react';

export function useFormValidation<T>({
  form,
  regexPatternsForRequiredPair,
}: {
  form: FormInstance;
  regexPatternsForRequiredPair?: string[];
}) {
  const values = Form.useWatch<T>([], form);
  const [submittable, setSubmittable] = useState(false);

  const checkRegexPatternsForRequiredPair = useCallback(() => {
    if (!regexPatternsForRequiredPair) return true;

    const formData = form.getFieldsValue(true);
    const [primaryPattern, pairedPattern] = regexPatternsForRequiredPair;
    const filledPrimaryKeys = Object.keys(formData)
      .filter(key => new RegExp(primaryPattern).test(key))
      .filter(key => !!formData[key]);

    const hasIncompletePairs = filledPrimaryKeys.some(primaryKey => {
      const id = primaryKey.split('-')[1];
      const pairedKey = `${pairedPattern}-${id}`;
      const pairedValue = formData[pairedKey];
      return !pairedValue?.[0];
    });

    return filledPrimaryKeys.length > 0 && !hasIncompletePairs;
  }, [form, regexPatternsForRequiredPair]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() =>
        setSubmittable(
          regexPatternsForRequiredPair
            ? checkRegexPatternsForRequiredPair()
            : true,
        ),
      )
      .catch(error => {
        const hasNoErrors = error?.errorFields?.length === 0;
        setSubmittable(
          regexPatternsForRequiredPair
            ? checkRegexPatternsForRequiredPair() && hasNoErrors
            : hasNoErrors,
        );
      });
  }, [
    form,
    values,
    regexPatternsForRequiredPair,
    checkRegexPatternsForRequiredPair,
  ]);

  return { values, submittable };
}
